// An issue's place in the landing queue, as one line: its open proposal's
// step, or what its running landing waits for, linking to it on the Board.
// While it is not landing, a person may withdraw it with a reason
// (withdraw_proposal): it ends, and its session is not prompted, as for a
// proposal that will never land (Poulpe-562). Under it, the runner it waits
// on while that runner is offline (Poulpe-367). While it is open, a person
// may mark it urgent, or normal again, with a reason (Poulpe-674), and the
// line says what urgency does where it stands. While its check, by hand or
// the queue's, waits or runs on a runner's machine, one more line says where
// it stands in line, behind what, and when it should start, by estimates
// from comparable runs alone (Poulpe-782).
import { LiveChecks } from '../checks/progress'
import { useId, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { Proposal } from '../api/types'
import { RETURN_NOTE_MAX } from '../api/types'
import { useMounted } from '../components/layout'
import { needsServer } from '../components/needsServer'
import { toast } from '../components/toasts'
import { useBoardState, useStore } from '../store/context'
import { ProposalWaitsOn } from '../runners/waits'
import { issueProposal } from '../store/store'
import { UrgencyBadge, UrgencyControl, urgencyEffect } from './Urgency'
import { standingOf, standingWords } from './machineWords'
import { useQueueDetail } from './QueueDetail'
import { proposalPath, short, stepName, waitingName } from './words'

export function IssueQueue({ issueId }: { issueId: string }) {
  const { proposals, landings, projects, runs } = useBoardState()
  const [withdrawing, setWithdrawing] = useState(false)
  const found = issueProposal(proposals, landings, issueId)
  // A check by hand on the issue that lasts, or the proposal's own check.
  const checking = [...runs.values()].find((run) => run.issue === issueId && run.kind === 'check' && (run.status === 'running' || run.status === 'waiting'))
  const checked = found && (found.landing || found.proposal.step === 'check') ? found.proposal : null
  const project = checked?.project_id ?? checking?.project
  const line = project ? <IssueCheckLine project={project} issueId={issueId} proposalId={found?.proposal.id ?? null} /> : null
  if (!found) return line && <div className="issue-queue">{line}</div>
  const { proposal, landing } = found
  const withdrawable = landing?.state === 'deferred' || (!landing && proposal.step !== 'landing')
  const effect = urgencyEffect(proposal)
  return (
    <div className="issue-queue">
      <p className="issue-queue-line">
        <Link to={proposalPath(proposal.id, projects.get(proposal.project_id)?.key)}>
          {landing ? 'Landing' : 'Proposed'} <code>{short(proposal.head)}</code>: {landing ? waitingName(landing) : stepName(proposal.step)}
        </Link>
        <UrgencyBadge proposal={proposal} />
        {landing ? <LiveChecks landing={landing.id} /> : <LiveChecks proposal={proposal.id} />}
        {withdrawable && !withdrawing && (
          <button type="button" className="button button-small button-quiet issue-queue-withdraw" onClick={() => setWithdrawing(true)}>Withdraw…</button>
        )}
        {!withdrawing && <UrgencyControl proposal={proposal} />}
      </p>
      {line}
      <ProposalWaitsOn proposal={proposal} landing={landing} />
      {effect && <p className="issue-queue-urgency">{effect}</p>}
      {withdrawable && withdrawing && <Withdraw key={proposal.id} proposal={proposal} onDone={() => setWithdrawing(false)} />}
    </div>
  )
}

/** Where the issue's check stands on its machine, read while it shows. */
function IssueCheckLine({ project, issueId, proposalId }: { project: string; issueId: string; proposalId: string | null }) {
  const { detail } = useQueueDetail(project)
  const standing = standingOf(detail, (check) => !check.anonymous && (check.issue?.id === issueId || (proposalId !== null && check.proposal === proposalId)))
  if (!standing || !detail) return null
  const words = standingWords(standing, detail.measured_at)
  const running = standing.place === null
  return <p className="issue-check-line" aria-label="Where its check stands">
    <span className={`checks-dot ${running ? 'is-running' : 'is-waiting'}`} aria-hidden="true" />
    <span>
      {words.what ? <>Your check <code>{words.what}</code></> : 'Your whole check'} {running ? 'runs' : <>waits: <b>{words.state}</b></>} {words.where}{words.when && `, ${words.when}`}
    </span>
    {words.behind && <span className="muted">{words.behind}</span>}
  </p>
}

/** Why the proposal is withdrawn, for its issue's record; its session is not told. */
function Withdraw({ proposal, onDone }: { proposal: Proposal; onDone: () => void }) {
  const store = useStore()
  const { offline } = useBoardState()
  const mounted = useMounted()
  const reasonId = useId()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const send = async (event: FormEvent) => {
    event.preventDefault()
    if (busy || !reason.trim()) return
    setBusy(true)
    const outcome = await store.withdrawProposal(proposal.id, reason.trim())
    // Withdrawn, the proposal ends and its line leaves the page.
    if (outcome.ok) toast(`Withdrew proposal ${short(proposal.head)}.`)
    else toast(outcome.message, 'error')
    if (mounted.current) setBusy(false)
  }
  return <form className="issue-withdraw" onSubmit={(event) => void send(event)}>
    <label className="visually-hidden" htmlFor={reasonId}>Why withdraw this proposal</label>
    <textarea id={reasonId} value={reason} onChange={(event) => setReason(event.target.value)} rows={2} maxLength={RETURN_NOTE_MAX}
      placeholder="Why will it never land? Its session is not told." autoFocus disabled={busy} />
    <div className="issue-withdraw-actions">
      <button type="button" className="button button-small button-quiet" onClick={onDone} disabled={busy}>Cancel</button>
      <button type="submit" className="button button-small button-primary" disabled={busy || offline || !reason.trim()} {...needsServer(offline)}>Withdraw proposal</button>
    </div>
  </form>
}
